import dayjs from "dayjs";
import { IActivity, IBeneficiary } from "./TemplateMovementsCheck.types";
import { ROTULO_CRITICADO_CURTO, ROTULO_RETIDO } from "../retencao-vocabulario";

// D4 (spec 2026-10-13): o status CONTINGENCY_APPLIED cobre o clique da corretora e o
// fecho pela carteira QBem; quem confirmou fica no histórico (observation).
export const ROTULO_CONCLUIDO_FORA = "Concluído fora da plataforma";

const ABERTURA_CONTINGENCIA =
  /(?:solicitada à corretora em|em verificação desde) (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?)/g;
const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * Idade do aviso de conclusão manual, pela abertura gravada na observação
 * (markContingency, backend). D6: a gravidade cresce com o tempo aberto, nunca
 * diminui com o número de pessoas; vale a abertura mais antiga.
 * Até 7 dias normal (info); mais de 7, aviso (warning); mais de 15, crítico (error).
 */
export function contingencyAge(
  observations: Array<string | null | undefined>,
  now: Date = new Date(),
): { dias: number | null; severity: "info" | "warning" | "error" } {
  const aberturas = observations
    // why: markContingency acrescenta à observação; num segundo episódio (devolvido ao
    // fluxo e reexportado) a linha traz duas aberturas, e vale a última.
    .map((o) => Array.from(o?.matchAll(ABERTURA_CONTINGENCIA) ?? []).at(-1)?.[1])
    .map((iso) => (iso ? Date.parse(iso) : NaN))
    .filter((t) => Number.isFinite(t));
  if (aberturas.length === 0) return { dias: null, severity: "warning" };
  const dias = Math.max(0, Math.floor((now.getTime() - Math.min(...aberturas)) / DIA_MS));
  return { dias, severity: dias > 15 ? "error" : dias > 7 ? "warning" : "info" };
}

export function formatDate(
  dateToFormat: string,
  formatType?: "timestamp" | "date" | "timestamp_2"
) {
  try {
    if (!dayjs(dateToFormat).isValid()) throw new Error();

    const date = dayjs(dateToFormat);

    const type = !formatType ? "date" : formatType;

    return type === "date"
      ? date.format(`DD/MM/YYYY`)
      : date.format("DD/MM/YYYY - HH:mm:ss");
  } catch (error) {
    const type = !formatType ? "date" : formatType;

    return type === "date"
      ? "-"
      : type === "timestamp"
      ? "--/--/---- --:--:--"
      : "-";
  }
}

export function formatType(payload: string) {
  if (payload === "EMPLOYEE") return "Titular";

  if (payload === "DEPENDENT") return "Dependente";

  return "-";
}

export function formatMovementType(payload: string) {
  if (payload === "inclusion") return "Inclusão";

  if (payload === "exclusion") return "Exclusão";

  if (payload === "change") return "Alteração";

  if (payload === "transfer") return "Transferência";

  if (payload === "maintenance") return "Manutenção";

  return "-";
}

export function formatStatus(status: string, holdLabel: string | null = null) {
  if (status === "READY")
    return {
      label: "Pronto",
      backgroundColor: "#0288D114",
      color: "#01579B",
    };

  if (status === "SKIPED")
    return {
      label: ROTULO_RETIDO,
      backgroundColor: "#BA1A1A14",
      color: "#BA1A1A",
    };

  // why: "Em espera" tem um significado por causa: vigência futura resolve sozinha com
  // o relógio; campanha depende de a pessoa responder. O rótulo do tipo vem do
  // backend (campaignHold.label), nunca de lista daqui.
  if (status === "DELAYED")
    return {
      label: holdLabel ? `Em espera (${holdLabel})` : "Em espera",
      backgroundColor: "#EF6C0014",
      color: "#EF6C00",
    };

  if (status === "RETAINED")
    return {
      label: ROTULO_CRITICADO_CURTO,
      backgroundColor: "#0F0F1E14",
      color: "#0F0F1E",
    };

  if (status === "CONTINGENCY")
    return {
      label: "Conclusão manual",
      backgroundColor: "#ED6C0214",
      color: "#8C4A00",
    };

  if (status === "CONTINGENCY_APPLIED")
    return {
      label: ROTULO_CONCLUIDO_FORA,
      backgroundColor: "#2E7D3214",
      color: "#1B5E20",
    };

  return {
    label: "-",
    backgroundColor: "#F5F5F5",
    color: "#616161",
  };
}

export function formatActivityStatus(status: string) {
  if (status === "IDLE")
    return {
      label: "Aguardando",
      backgroundColor: "#191C1A14",
      color: "#191C1A",
    };

  if (status === "PENDING")
    return {
      label: "Pendente",
      backgroundColor: "#EF6C0014",
      color: "#EF6C00",
    };

  if (status === "SUCCESS")
    return {
      label: "Executado",
      backgroundColor: "#2E7D3214",
      color: "#2E7D32",
    };

  if (status === "FAILED")
    return {
      label: "Falhou",
      backgroundColor: "#ff7d7d3b",
      color: "#a30000",
    };

  return {
    label: "Aguardando",
    backgroundColor: "#F5F5F5",
    color: "#616161",
  };
}

export function sortApps(activities: IActivity[]) {
  const order = [
    "dag_excecao",
    "app_transfer",
    "app_change",
    "app_exclusion",
    "app_inclusion",
  ];

  const priority = Object.fromEntries(order.map((slug, i) => [slug, i]));

  return activities.sort((a, b) => {
    const pa = priority[a.slug] ?? Number.MAX_SAFE_INTEGER;
    const pb = priority[b.slug] ?? Number.MAX_SAFE_INTEGER;
    return pa - pb;
  });
}

/** why: app_approve tem fluxo próprio (o botão Aprovar); app_campaign abre o monitor por tipo (F3). */
export function appsDoRodape(activities: IActivity[]) {
  return sortApps(activities.filter((a) => a.type === "APP" && a.slug !== "app_approve"));
}

/** why: cópia; o sort ordena no lugar e a lista recebida é o cache da consulta de Conferir. */
export function atividadesRelacionadas(activities: IActivity[]) {
  return sortRelatedActivities([...activities]);
}

export function sortRelatedActivities(activities: IActivity[]) {
  const order = [
    "prepare_people_data",
    "process_contract_rules",
    "employee_movements",
    "app_approve",
    "app_inclusion",
    "app_exclusion",
    "app_change",
    "app_transfer",
    "dag_excecao",
  ];

  const priority = Object.fromEntries(order.map((slug, i) => [slug, i]));

  return activities.sort((a, b) => {
    const pa = priority[a.slug] ?? Number.MAX_SAFE_INTEGER;
    const pb = priority[b.slug] ?? Number.MAX_SAFE_INTEGER;
    return pa - pb;
  });
}

type HoldComRotulo = { label?: string } | null | undefined;

/** why: valor de filtro do status: o status na massa segue DELAYED; a campanha que retém entra pelo rótulo. */
export function statusFilterValue(status: string | null, hold: HoldComRotulo): string | null {
  return status === "DELAYED" && hold?.label ? `DELAYED_CAMPAIGN:${hold.label}` : status;
}

/** invariant: opções do filtro nascidas dos rótulos presentes nas linhas, em ordem alfabética. */
export function campaignHoldFilterOptions(rows: { campaignHold?: HoldComRotulo }[]) {
  const labels = new Set<string>();
  rows.forEach((r) => r.campaignHold?.label && labels.add(r.campaignHold.label));
  return [...labels]
    .sort((a, b) => a.localeCompare(b, "pt-BR"))
    .map((label) => ({ value: `DELAYED_CAMPAIGN:${label}`, label: `Em espera (${label})` }));
}

// M1/D13: o que o RH escolhe aprovar. Transferência é um tipo só: o par isTransfer
// (inclusão + exclusão) nunca é separado, nem entra em Inclusões ou Exclusões.
export type TipoAprovavel = "inclusion" | "exclusion" | "change" | "transfer";
export const TIPOS_APROVAVEIS: TipoAprovavel[] = ["inclusion", "exclusion", "change", "transfer"];

export function tipoDaLinha(p: Pick<IBeneficiary, "movementType" | "isTransfer">): TipoAprovavel | null {
  const tipo = p.movementType?.toLowerCase();
  if (tipo === "transfer" || p.isTransfer) return "transfer";
  return tipo === "inclusion" || tipo === "exclusion" || tipo === "change" ? tipo : null;
}

export function filtrarPorTipos(prontos: IBeneficiary[], tipos: Set<TipoAprovavel>): IBeneficiary[] {
  return prontos.filter((p) => {
    const tipo = tipoDaLinha(p);
    return tipo !== null && tipos.has(tipo);
  });
}

export function contarPorTipo(prontos: IBeneficiary[]): Record<TipoAprovavel, number> {
  const contagem = { inclusion: 0, exclusion: 0, change: 0, transfer: 0 };
  prontos.forEach((p) => {
    const tipo = tipoDaLinha(p);
    if (tipo) contagem[tipo] += 1;
  });
  return contagem;
}

// Contagem enviada ao servidor e mostrada nos cards: pelo movementType real da linha
// (o par de transferencia conta 1 inclusao e 1 exclusao), igual ao backend.
export function contarPorTipoReal(linhas: Pick<IBeneficiary, "movementType">[]): Record<TipoAprovavel, number> {
  const contagem = { inclusion: 0, exclusion: 0, change: 0, transfer: 0 };
  linhas.forEach((p) => {
    const tipo = p.movementType?.toLowerCase();
    if (tipo === "inclusion" || tipo === "exclusion" || tipo === "change" || tipo === "transfer") contagem[tipo] += 1;
  });
  return contagem;
}

// Par de transferencia: se uma metade esta sem documento, a outra tambem fica fora do lote
// (o backend recusa o lote inteiro se o par nao vai completo).
export function completarParesSemDocumento(prontos: IBeneficiary[], semDocumento: IBeneficiary[]): IBeneficiary[] {
  const cpf = (p: IBeneficiary) => String(p.taxPayerId).replace(/\D/g, "");
  const cpfsComParFora = new Set(semDocumento.filter((p) => p.isTransfer).map(cpf));
  const extras = prontos.filter((p) => p.isTransfer && cpfsComParFora.has(cpf(p)) && !semDocumento.includes(p));
  return [...semDocumento, ...extras];
}

/** Id do template "Exportar Movimentação" do contrato (mesmo critério de ListActivities); null se não houver. */
export function templateIdDoExportar(templates: { id: string; name: string }[] | undefined): string | null {
  return templates?.find((t) => t.name === "Exportar Movimentação")?.id ?? null;
}

/**
 * A pasta da pessoa na aba Documentos da empresa, filtrada no contrato (spec Repositório 4.4).
 * O CPF vai no estado da rota (`history.push({ ..., state: { cpf } })`), nunca na URL.
 */
export function linkDaPasta(organizationId: string, workspaceId: string): { pathname: string; search: string } {
  return { pathname: `/documentos/${organizationId}`, search: `?contrato=${workspaceId}` };
}
